<script lang="ts">
	import type { SearchAddress$input, SearchAddress$result } from '$houdini';
	import type { InputControl } from '$lib/components/atoms/input/types';
	import type { Coordinates, UserAction } from '$lib/components/map/types';
	import type { SearchAddressResult } from '$lib/components/search/address/types';
	import type { WayPoint } from '$lib/components/search/itinerary/types';
	import type { ActiveWaypoint, Result } from '$lib/components/search/types';
	import { SearchAddressStore } from '$houdini';
	import { isMyLocation, myLocResult } from '$lib/components/search/itinerary/myLocResult';
	import { computeMyLocation } from '$lib/components/search/itinerary/userLocation';
	import SearchInputWithQuery from '$lib/components/search/SearchInputWithQuery.svelte';
	import {
		buildSearchbarAutocompletionPayload,
		trackSearchbarAutocompletion,
		trackSearchbarFocus,
		trackSearchbarOnFocusUtils,
	} from '$lib/services/tracking/googleAnalytics/searchbar';
	import * as m from '$paraglide/messages';
	import { onMount, type Snippet } from 'svelte';

	interface Props {
		proximity: Coordinates;
		waypoint: WayPoint;
		controls?: InputControl[];
		includeMyLocationOption?: boolean;
		placeholder?: string;
		onclick?: () => void;
		onSelect?: (waypoint: WayPoint) => void;
		onActivate?: (waypoint: ActiveWaypoint) => void;
		onDeactivate?: () => void;
		beforeListItems?: Snippet<[onmouseover: (index: number) => void, activeItemIndex: number, value: string]>;
	}

	const {
		proximity,
		waypoint,
		controls,
		includeMyLocationOption,
		placeholder,
		onclick,
		onSelect,
		onActivate,
		onDeactivate,
		beforeListItems,
	}: Props = $props();

	let queryStore: SearchAddressStore | undefined = $state();
	let inputIsFocused = $state(false);

	let input: SearchInputWithQuery<
		SearchAddress$result,
		SearchAddress$input,
		(queryData: SearchAddress$result) => SearchAddress$result['searchAddress']
	> | undefined = $state();

	const { setIsTriggered, trackingIsTriggered } = trackSearchbarOnFocusUtils();

	onMount(() => {
		queryStore = new SearchAddressStore();
	});

	function addressToWayPoint(result: SearchAddressResult | null, userAction: UserAction, isFromStorage: boolean): WayPoint {
		const isMyLoc = isVirtualMyLocation(result);

		if (userAction) {
			trackSearchbarAutocompletion(
				buildSearchbarAutocompletionPayload({
					selected: result,
					isFromStore: isFromStorage,
					from: waypoint,
				}),
			);
		}

		return {
			...waypoint,
			coordinates: isMyLoc ? 'MY_LOC' : result?.mapLocation.location,
			label: isMyLoc ? m.itinerary__current_location() : result?.formattedName,
			isMyLocation: isMyLoc,
			addressType: result?.entityType ?? undefined,
		};
	}

	function isVirtualMyLocation(result: SearchAddressResult | null): boolean {
		const isMyLoc = !!(result && isMyLocation(result));

		if (isMyLoc) {
			computeMyLocation();
		}

		return isMyLoc;
	}

	function getWaypointProps(wp: WayPoint) {
		if (wp.isArrival) {
			return {
				icon: 'itinerary-arrival',
				id: 'arrival',
			};
		}
		if (wp.index === 0) {
			return {
				icon: 'location-on',
				id: 'departure',
			};
		}
		return {
			icon: `counter-${wp.index}`,
			id: `step-${wp.index}`,
		};
	}

	function handleSelect(result: Result | null, userAction: UserAction, isFromStorage: boolean) {
		onSelect?.(addressToWayPoint(result as SearchAddressResult, userAction, isFromStorage));
	}

	function getInputName() {
		return (waypoint.isArrival && m.route__arrival()) || (waypoint.index === 0 && m.route__departure()) || `${m.route__step()} ${waypoint.index}`;
	}
	const { icon, id } = $derived(getWaypointProps(waypoint));
</script>

<SearchInputWithQuery
	{id}
	{icon}
	{queryStore}
	{controls}
	bind:this={input}
	{placeholder}
	{beforeListItems}
	defaultResults={includeMyLocationOption ? [myLocResult()] : undefined}
	resultToLabel={(searchAddress) => {
		return {
			label: 'formattedName' in searchAddress ? searchAddress.formattedName : '',
			additionalLabel:
				('contextualizedName' in searchAddress && searchAddress.contextualizedName) || undefined,
		};
	}}
	queryToVariables={q => ({ query: q, proximity })}
	getStoreResults={results => results.searchAddress}
	initialValue={waypoint.label}
	onSelect={({ result, userAction, isFromStore }) => handleSelect(result, userAction, isFromStore)}
	hasTrackingEnabled
	hasHistoryEnabled
	onActivate={() => {
		inputIsFocused = true;
		if (waypoint.isMyLocation) {
			// Reset the input if we are using the user location
			// We do not want the user to have to remove "your location" or to
			// be able to modify the input with "your location" inside
			input?.clear();
		}

		onActivate?.({ inputName: getInputName() });
	}}

	onDeactivate={() => {
		inputIsFocused = false;
		setIsTriggered(false);
		onDeactivate?.();
	}}
	onclick={() => {
		if (!trackingIsTriggered && inputIsFocused) {
			trackSearchbarFocus();
			setIsTriggered(true);
		}
		onclick?.();
	}}
/>
